import type { NitroFetchRequest, NitroFetchOptions } from 'nitropack';
import { defu } from 'defu';
import { ORG_MANAGEMENT_KEY } from './useClientManagementNav';

// useAuth / useAuthFetch are Nuxt auto-imports (see useFeatureFlags.ts and
// useClientManagementNav.ts's useNuxtApp for the same bare-identifier pattern — unlike
// useAuthServiceApi.ts, which imports useAuth explicitly from '#imports') — left unimported
// here so this module's one pure, synchronous export (organizationServiceBaseUrl) stays
// importable from a plain Node test without pulling in '#imports', which only resolves
// inside the Nuxt build/test pipeline.

interface NavigationEntry {
  text: string;
  api_url?: string | null;
  [key: string]: unknown;
}

interface NavUserProfile {
  navigation?: NavigationEntry[];
}

/**
 * organization-service's base URL is not frontend config — it comes from the
 * `organizations` navigation entry's `api_url`, served by auth-service per MP-3382.
 * A bare origin (no path); auth-service does not run it through the subdomain rewrite
 * it applies to nav `url`s (see OauthApplication#nav_url), and unlike the auth-service
 * wrappers in useAuthServiceApi.ts, we must not wrap it in useUrlWithSubdomain either —
 * organization-service is a single global host, not a per-client subdomain.
 *
 * `navigation` is a userinfo-only claim in auth-service
 * (doorkeeper_openid_connect.rb: `response: %i[user_info]`), not part of the id_token.
 * It still lands on `useAuth().userProfile` because @apptegy/nuxt-auth's UserManager is
 * configured with `loadUserInfo: true`, which merges the userinfo response into the
 * oidc-client-ts profile.
 */
export const organizationServiceBaseUrl = (userProfile?: NavUserProfile | null): string | null =>
  userProfile?.navigation?.find((entry) => entry.text === ORG_MANAGEMENT_KEY)?.api_url ?? null;

/*
 * A plain Error with the status assigned rather than Nuxt's createError, so this module stays
 * importable from the node unit project. apiErrorStatus reads `statusCode` either way.
 */
export const noGrantError = () =>
  Object.assign(
    new Error(
      'useOrganizationServiceApi: no organizations nav entry on the current profile — the operator has no live organization_service grant.',
    ),
    { statusCode: 403 },
  );

/**
 * Wraps useAuthFetch (not raw $fetch) so calls to organization-service's admin surface
 * carry the same Authorization: Bearer <id_token> header and 401 silent-refresh/retry
 * as every other authenticated call in this app. No new token type, no new scope: the
 * admin surface authorizes on the id_token's `roles.organization_service` claim plus a
 * mirrored principal in organization-service, both already covered by the existing
 * id_token.
 *
 * `request` is the path under the admin surface, e.g. '/clients' or
 * `/organizations/${id}/descendants`.
 *
 * Throws if the operator has no `organizations` nav entry (no live grant) — there is no
 * base URL to call in that case. The error carries `statusCode: 403` because it is the same
 * "not granted" condition organization-service itself answers with a 403, so callers render
 * one state for both.
 */
export async function useOrganizationServiceApi<T>(
  request: NitroFetchRequest,
  opts: NitroFetchOptions<NitroFetchRequest> = {},
) {
  const { userProfile } = useAuth();
  const baseUrl = organizationServiceBaseUrl(userProfile.value);

  if (!baseUrl) throw noGrantError();

  const options: Partial<NitroFetchOptions<NitroFetchRequest>> = {
    baseURL: `${baseUrl}/api/v1/admin`,
  };
  return useAuthFetch<T>(request, defu(opts, options));
}
